import React, { useEffect, useState } from 'react';
import {
    MapPin, Clock, Ticket, ChevronRight, Calendar, Phone, Mail, Users,
    Smartphone, SmartphoneNfc, Headphones, Sparkles, Check
} from 'lucide-react';
import { fetchData } from '../helpers/api';
import usePageData from '../helpers/usePageData';
import TarifsList from '../components/TarifsList';

const VisitPage = () => {
    const [settings, setSettings] = useState(null);
    const { data: pageData, loading: pageLoading } = usePageData('visiter');

        useEffect(() => {
            fetchVisitData();
        }, []);

        const fetchVisitData = async () => {
            try {
                const promises = [
                    fetchData('/api/setting')
                ]
                const [settingsData] = await Promise.all(promises);

                setSettings(settingsData);
            } catch (e) {
                console.error('Failed fetching data: ', e);
            }
        };

    if (pageLoading || !pageData) return null;

    return (
        <div className="visiter-page">
            {/* Hero Section */}
            <section className="bg-light py-5">
                <div className="container py-3">
                    <div className="row align-items-center">
                        <div className="col-lg-6 mb-4 mb-lg-0">
                            <h1 className="display-4 fw-bold mb-4">
                                {pageData.hero.title.includes('MACTe') ? (
                                    <>{pageData.hero.title.split('MACTe')[0]}<span className="text-warning">MACTe</span>{pageData.hero.title.split('MACTe')[1]}</>
                                ) : pageData.hero.title}
                            </h1>
                            <p className="fs-5 text-muted mb-4">
                                {pageData.hero.subtitle}
                            </p>
                            <a
                                href={settings?.defaultBookingUrl || `#`}
                                className="btn btn-warning btn-lg rounded-pill px-4 d-inline-flex align-items-center"
                                target="_blank"
                            >
                                {pageData.hero.buttonText || 'Réserver une visite'}
                                <ChevronRight size={20} className="ms-2" />
                            </a>
                        </div>
                        <div className="col-lg-6">
                            <div className="bg-white shadow-lg rounded p-4">
                                <h2 className="fs-4 fw-bold mb-3">Informations Pratiques</h2>
                                <div className="d-flex flex-column gap-3">
                                    <div className="d-flex align-items-start">
                                        <Clock size={24} className="text-warning me-3 mt-1" />
                                        <div>
                                            <h3 className="fs-5 fw-semibold mb-1">Horaires</h3>
                                            <p className="mb-0">{pageData.infos.horaires}</p>
                                            <p className="small text-muted">{pageData.infos.fermeture}</p>
                                            <p className="small text-muted">{pageData.infos.dernierDepart}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-start">
                                        <MapPin size={24} className="text-warning me-3 mt-1" />
                                        <div>
                                            <h3 className="fs-5 fw-semibold mb-1">Adresse</h3>
                                            <p className="mb-0">{pageData.infos.adresse1}</p>
                                            <p className="mb-0">{pageData.infos.adresse2}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-start">
                                        <Phone size={24} className="text-warning me-3 mt-1" />
                                        <div>
                                            <h3 className="fs-5 fw-semibold mb-1">Téléphone</h3>
                                            <p className="mb-0">{pageData.infos.telephone}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-start">
                                        <Mail size={24} className="text-warning me-3 mt-1" />
                                        <div>
                                            <h3 className="fs-5 fw-semibold mb-1">Email</h3>
                                            <p className="mb-0">{pageData.infos.email}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Smart Visit Section */}
            {pageData.smartVisit && (
                <section className="py-5 bg-white">
                    <div className="container py-3">
                        <div className="row align-items-center gx-5 mb-5">
                            <div className="col-lg-7 mb-4 mb-lg-0">
                                {pageData.smartVisit.badge && (
                                    <span className="badge bg-warning text-dark rounded-pill px-3 py-2 mb-3 text-uppercase">
                                        {pageData.smartVisit.badge}
                                    </span>
                                )}
                                <h2 className="display-5 fw-bold mb-4">{pageData.smartVisit.title}</h2>
                                {(pageData.smartVisit.intro || []).map((paragraph, index) => (
                                    <p key={index} className="fs-5 text-muted">{paragraph}</p>
                                ))}
                            </div>
                            <div className="col-lg-5 text-center">
                                {pageData.smartVisit.image && (
                                    <img
                                        src={pageData.smartVisit.image}
                                        alt="Smart Visit - visite interactive avec votre smartphone"
                                        className="img-fluid rounded shadow-lg"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="row g-4 mb-4">
                            {(pageData.smartVisit.highlights || []).map((highlight, index) => {
                                const icons = [Smartphone, SmartphoneNfc, Headphones, Sparkles];
                                const Icon = icons[index % icons.length];
                                return (
                                    <div key={index} className="col-md-6 col-lg-3">
                                        <div className="bg-light p-4 rounded h-100 border-start border-warning border-4 hover-shadow transition">
                                            <Icon size={32} className="text-warning mb-3" />
                                            <h3 className="fs-5 fw-bold mb-2">{highlight.title}</h3>
                                            <p className="text-muted mb-0">{highlight.text}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {pageData.smartVisit.features?.length > 0 && (
                            <div className="bg-light rounded p-4">
                                <div className="row">
                                    {pageData.smartVisit.features.map((feature, index) => (
                                        <div key={index} className="col-md-6 d-flex align-items-start mb-2">
                                            <Check size={20} className="text-warning me-2 mt-1 flex-shrink-0" />
                                            <p className="mb-0">{feature}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Tarification Section */}
            <section className="py-5 bg-white">
                <div className="container py-3">
                    <div className="row gx-5">
                        <div className="col-lg-6 mb-5 mb-lg-0">
                            <h2 className="display-5 fw-bold mb-4">TARIFICATION</h2>
                            <div className="d-flex flex-column gap-4 mt-4">
                                <div
                                    className="bg-light p-4 rounded border-start border-warning border-4 hover-shadow transition"
                                >
                                    <div className="d-flex">
                                        <div>
                                            <TarifsList tarifs={pageData.tarifs} note={pageData.tarifsNote} />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <a href={settings?.defaultBookingUrl || `#`} target="_blank" className="btn btn-dark btn-lg rounded-pill px-4 d-inline-flex align-items-center">
                                        {pageData.bookingButtonText || 'Réserver ma visite'}
                                        <ChevronRight size={20} className="ms-2" />
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <iframe
                                width="100%"
                                height="450"
                                style={{ border: 0 }}
                                loading="lazy"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyC18sy8vUnE-K4K2KnRmH0NNnjWFJcv6mM&q=${pageData.mapsQuery}`}>
                            </iframe>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default VisitPage;
